import { useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '../config/firebase';
import { industries } from '../components/common/IndustrySelect';

interface CreateBrandPayload {
  name: string;
  description: string;
  logo: string;
  website: string;
  industryId: string;
  values: string[];
  keywords: string[];
  languages: string[];
  contentTone: string;
  voice: string;
  uniqueSellingPoints: string[];
  recommendedFormats: string[];
  socialMedias: Array<{
    platform: string;
    frequency: string;
  }>;
  themes: string[];
  contentGaps: string[];
  engagementTactics: string[];
  growthOpportunities: string[];
  summary?: {
    recap: string;
    takeaways: string[];
  };
  visualIdentity?: {
    colorPalette: {
      primary: string[];
      secondary: string[];
      accent: string[];
      neutral: string[];
      recommended?: {
        complementary: string[];
        improvements: string;
      };
    };
    typography: {
      headings: {
        h1: string;
        h2: string;
        h3: string;
      };
      body: {
        primary: string;
        secondary: string;
      };
      specialty: {
        buttons: string;
        captions: string;
        navigation: string;
      };
      fallbacks: string[];
      recommendations?: {
        improvements: string;
        hierarchy: string;
      };
    };
  };
  visualContentStrategy?: {
    colorUsage: {
      socialMediaPosts: string;
      callToActions: string;
      backgrounds: string;
      seasonalAdaptations: string;
    };
    typographyGuidelines: {
      socialMediaHeaders: string;
      bodyText: string;
      quotesTestimonials: string;
      hashtagsKeywords: string;
    };
    visualElementsToEmphasize: string[];
    designConsistencyTips: string[];
    brandExtensions: {
      socialTemplates: string;
      logoUsage: string;
      imageFilters: string;
    };
  };
  contentStrategy?: {
    themes: string[];
    contentGaps: string[];
    engagementTactics: string[];
    growthOpportunities: string[];
    recommendedFormats?: string[];
    recommendedPlatforms?: Array<{
      platform: string;
      postingFrequency: string;
    }>;
  };
  overview?: {
    summary: string;
    takeaways: string[];
  };
  goToMarketStrategy?: {
    short: string;
    targetSegments: string[];
    keyPersonaTypes: string[];
    marketScope: string;
    priorityMarkets: string[];
    businessModel: string;
    distributionType: string;
  };
}

export const useBrandCreate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBrandPayload) => {
      // Function to get a fresh token with force refresh option
      const getAuthToken = async (forceRefresh = false) => {
        console.log(`Getting auth token${forceRefresh ? ' (forced refresh)' : ''}`);
        try {
          if (!auth.currentUser) {
            console.error('No authenticated user found');
            throw new Error('Authentication required. Please log in again.');
          }
          
          const token = await auth.currentUser.getIdToken(forceRefresh);
          if (!token) {
            throw new Error('Failed to obtain authentication token');
          }
          return token;
        } catch (error) {
          console.error('Error getting auth token:', error);
          throw error;
        }
      };

      // Transform the data to match the format expected by the API
      const selectedIndustry = industries.find(ind => ind.id === data.industryId);
      const apiData = {
        name: data.name,
        description: data.description,
        logo: data.logo,
        website: data.website,
        // Send both industryId and industry name
        industryId: data.industryId,
        industry: selectedIndustry?.name || data.industryId,
        values: data.values,
        keywords: data.keywords,
        languages: data.languages,
        contentTone: data.contentTone,
        voice: data.voice,
        uniqueSellingPoints: data.uniqueSellingPoints,
        
        // Include socialMedias as expected by the API
        socialMedias: data.socialMedias || [],
        themes: data.themes || [],
        contentGaps: data.contentGaps || [],
        engagementTactics: data.engagementTactics || [],
        growthOpportunities: data.growthOpportunities || [],
        recommendedFormats: data.recommendedFormats || [],
        
        // Include summary directly
        summary: data.summary || { recap: '', takeaways: [] },
        
        // Include visual identity if provided
        ...(data.visualIdentity && { visualIdentity: data.visualIdentity }),
        
        // Include visual content strategy if provided
        ...(data.visualContentStrategy && { visualContentStrategy: data.visualContentStrategy }),
        
        // Include go-to-market strategy if provided (NEW!)
        ...(data.goToMarketStrategy && { goToMarketStrategy: data.goToMarketStrategy }),
        
        // Format the content strategy properly
        contentStrategy: {
          themes: data.themes || [],
          contentGaps: data.contentGaps || [],
          engagementTactics: data.engagementTactics || [],
          growthOpportunities: data.growthOpportunities || [],
          recommendedFormats: data.recommendedFormats || [],
          recommendedPlatforms: data.socialMedias.map(sm => ({
            platform: sm.platform,
            postingFrequency: sm.frequency
          }))
        },
        
        // Format the overview properly
        overview: data.summary ? {
          summary: data.summary.recap || '',
          takeaways: data.summary.takeaways || []
        } : undefined
      };
      
      console.log('Creating brand with data:', JSON.stringify(apiData, null, 2));
      
      // First attempt with regular token
      try {
        // Get token without force refresh first
        const token = await getAuthToken(false);
        console.log('Token available, length:', token.length);
        
        const response = await fetch('https://api.socialness.ai/management/brands', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(apiData)
        });
        
        console.log('API response status:', response.status);
        
        // Try to get the response text for better error logging
        const responseText = await response.text();
        console.log('API response text:', responseText);
        
        // If we get an auth error (401) or user fetch error, try with a force-refreshed token
        if (response.status === 401 || 
            (response.status === 500 && responseText.includes('user_fetch_error'))) {
          
          console.log('Authentication issue detected. Attempting with forced token refresh...');
          
          // Second attempt with force-refreshed token
          try {
            // Force refresh the token
            const refreshedToken = await getAuthToken(true);
            console.log('Refreshed token available, length:', refreshedToken.length);
            
            const retryResponse = await fetch('https://api.socialness.ai/management/brands', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${refreshedToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(apiData)
            });
            
            console.log('Retry API response status:', retryResponse.status);
            
            const retryResponseText = await retryResponse.text();
            console.log('Retry API response text:', retryResponseText);
            
            if (!retryResponse.ok) {
              // Still failed even with refreshed token
              let errorMessage = `Failed to create brand (Status: ${retryResponse.status})`;
              
              try {
                const errorData = JSON.parse(retryResponseText);
                errorMessage = errorData.message || errorData.error || errorMessage;
                console.error('API error response data after token refresh:', errorData);
              } catch (parseError) {
                console.error('Could not parse error response as JSON:', retryResponseText);
              }
              
              throw new Error(errorMessage);
            }
            
            // Success on second attempt
            try {
              return retryResponseText ? JSON.parse(retryResponseText) : { success: true };
            } catch (parseError) {
              console.error('Failed to parse successful response:', parseError);
              return { success: true };
            }
          } catch (retryError) {
            console.error('Error during retry with refreshed token:', retryError);
            throw retryError;
          }
        }
        
        // Handle normal error response for first attempt
        if (!response.ok) {
          let errorMessage = `Failed to create brand (Status: ${response.status})`;
          
          // Try to parse the error as JSON
          try {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.message || errorData.error || errorMessage;
            console.error('API error response data:', errorData);
          } catch (parseError) {
            console.error('Could not parse error response as JSON:', responseText);
          }
          
          throw new Error(errorMessage);
        }
        
        // Parse the response if it has content (first attempt success)
        try {
          const result = responseText ? JSON.parse(responseText) : {};
          return result;
        } catch (parseError) {
          console.error('Failed to parse successful response:', parseError);
          return { success: true }; // Return a basic success object if parsing fails
        }
      } catch (error) {
        console.error('Network or parsing error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate and refetch brands query
      queryClient.invalidateQueries({ queryKey: ['brands'] });
    }
  });
};